import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, message, Space, Typography } from 'antd';
import { createExportPaymentIntent, confirmExportPurchase } from '../../services/PaymentServiceAnnuaire'; 

const { Text } = Typography;

// ✅ Définir COLORS ici
const COLORS = {
  primary: "#4bb0c0",
  secondary: "#f4a261",
  light: "#f8f9fa",
};

interface PaymentFormExportProps {
  amount: number;
  userId: number;
  professionName?: string;  // ← AJOUTER cette prop
  fileName?: string;        // ← AJOUTER cette prop
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentFormExport: React.FC<PaymentFormExportProps> = ({
  amount,
  userId,
  professionName,
  fileName,
  onSuccess,
  onCancel,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      message.error("Stripe n'est pas initialisé");
      return;
    }

    setLoading(true);
    try {
      // 1. Créer le PaymentIntent
      const { clientSecret } = await createExportPaymentIntent(amount, userId);
      
      // 2. Confirmer le paiement avec Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement)! },
      });

      if (stripeError) {
        console.error("❌ Erreur Stripe:", stripeError);
        message.error(stripeError.message || "Le paiement a échoué");
        return;
      }

      // 3. ✅ Confirmer l'achat auprès de votre backend
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log("✅ Paiement réussi, confirmation backend...");
        
        await confirmExportPurchase({
          paymentIntentId: paymentIntent.id,
          amount: amount,
          userId: userId,
          professionName: professionName,
          fileName: fileName
        });

        console.log("✅ Achat export confirmé avec succès");
        message.success('Paiement réussi !');
        onSuccess();
      } else {
        message.error("Le paiement n'a pas abouti");
      }

    } catch (error: any) {
      console.error("❌ Erreur paiement:", error);
      message.error(error.message || "Une erreur est survenue lors du paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Informations de paiement
        </Text>
        <div style={{ 
          padding: 12, 
          border: '1px solid #d9d9d9', 
          borderRadius: 4,
          backgroundColor: '#ffffff'
        }}>
          <CardElement 
            options={{ 
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              }
            }} 
          />
        </div>
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          Paiement sécurisé par Stripe
        </Text>
      </div>

      <div style={{ 
        marginBottom: 24, 
        padding: 16, 
        backgroundColor: COLORS.light, 
        borderRadius: 8, 
        textAlign: 'center' 
      }}>
        <Text type="secondary">Total à payer</Text>
        <div style={{ fontSize: 32, fontWeight: 'bold', color: COLORS.secondary }}>
          {amount} €
        </div>
      </div>

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={loading}>
          Annuler
        </Button>
        <Button 
          type="primary" 
          htmlType="submit" 
          loading={loading} 
          disabled={!stripe || loading}
          style={{ 
            backgroundColor: COLORS.primary, 
            borderColor: COLORS.primary,
            minWidth: 120
          }}
        >
          {loading ? 'Paiement...' : `Payer ${amount} €`}
        </Button>
      </Space>
    </form>
  );
};

export default PaymentFormExport;